import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { formatChargeCode, STICKER_BATCH_MAX, STICKER_BATCH_PRESETS, type QrSticker, type StickerTheme } from '@pulsepoint/shared';
import { Download } from 'lucide-react';
import { useState } from 'react';
import { ApiError } from '../../api/client';
import { ChoiceChip } from '../../components/ChoiceChip';
import type { DialogProps } from '../../components/dialogs/DialogProvider';
import { useToast } from '../../components/Toast';
import { colours } from '../../theme';
import { PdfPreview } from './PdfPreview';
import { stickerPlace, stickerSite, useDownloadStickers, useMakeStickers } from './api';

/** The two looks, as a small picture of each sticker to choose from. */
function ThemePicker({ value, onChange }: { value: StickerTheme; onChange(t: StickerTheme): void }) {
  const looks: { theme: StickerTheme; label: string; hint: string; bg: string; fg: string }[] = [
    { theme: 'light', label: 'Light', hint: 'White sticker', bg: colours.white, fg: colours.nearBlack },
    { theme: 'dark', label: 'Dark', hint: 'Night version, for dark chargers', bg: '#0E0E12', fg: '#FBFCFF' },
  ];
  return (
    <Stack direction="row" spacing={2} role="radiogroup" aria-label="Sticker look">
      {looks.map((l) => {
        const on = value === l.theme;
        return (
          <Box
            key={l.theme}
            component="button"
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(l.theme)}
            sx={{
              flex: 1,
              display: 'flex',
              gap: 1.5,
              alignItems: 'center',
              p: 1.5,
              textAlign: 'left',
              font: 'inherit',
              cursor: 'pointer',
              bgcolor: colours.white,
              border: `${on ? 2 : 1}px solid ${on ? colours.nearBlack : colours.hairline}`,
              borderRadius: 3,
              m: on ? 0 : '1px',
            }}
          >
            {/* A tiny sticker: 2:3, a white QR square, the code line. */}
            <Box aria-hidden sx={{ width: 32, height: 48, flexShrink: 0, bgcolor: l.bg, border: `1px solid ${colours.hairline}`, borderRadius: 0.5, display: 'grid', placeItems: 'center' }}>
              <Box sx={{ width: 16, height: 16, bgcolor: colours.white, border: `1px solid ${colours.hairline}`, borderRadius: 0.5, backgroundImage: `repeating-linear-gradient(90deg, ${colours.nearBlack} 0 2px, transparent 2px 4px)`, backgroundSize: '12px 12px', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }} />
              <Box sx={{ width: 18, height: 2, bgcolor: l.fg, mt: -1 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 500 }}>{l.label}</Typography>
              <Typography variant="body2" color="textSecondary">{l.hint}</Typography>
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
}

const plural = (n: number) => `${n} sticker${n === 1 ? '' : 's'}`;

/** Download stickers already made, as PDFs (a zip for more than one). */
export function DownloadStickersDialog({ ids, title, close }: { ids: string[]; title?: string } & DialogProps) {
  const [theme, setTheme] = useState<StickerTheme>('light');
  const download = useDownloadStickers();
  const toast = useToast();
  const go = async () => {
    try {
      const name = await download.mutateAsync({ ids, theme });
      toast(`Saved ${name}.`);
      close();
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Something went wrong.', 'error');
    }
  };
  return (
    <Dialog open onClose={() => !download.isPending && close()} fullWidth maxWidth="sm">
      <DialogTitle>{title ?? `Download ${plural(ids.length)}`}</DialogTitle>
      <DialogContent>
        <Typography color="textSecondary" sx={{ mb: 2.5 }}>
          {ids.length === 1 ? 'A PDF ready for the printer: 80 × 180 mm with 3 mm bleed.' : 'A zip file with a PDF for each sticker, ready for the printer: 80 × 180 mm with 3 mm bleed.'}
        </Typography>
        <ThemePicker value={theme} onChange={setTheme} />
        {ids.length > 100 && download.isPending && (
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            Making {ids.length} PDFs takes a little while (about 5 seconds per 100).
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => close()} disabled={download.isPending}>Cancel</Button>
        <Button variant="contained" startIcon={<Download size={20} strokeWidth={1.75} />} onClick={() => void go()} disabled={download.isPending}>
          {download.isPending ? 'Making PDFs…' : 'Download'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** A batch of new stickers, not on any charger yet: made, then downloaded for the printer. */
export function MakeStickersDialog({ close }: DialogProps) {
  const [preset, setPreset] = useState<number | 'custom'>(STICKER_BATCH_PRESETS[0]);
  const [custom, setCustom] = useState('');
  const [theme, setTheme] = useState<StickerTheme>('light');
  const make = useMakeStickers();
  const download = useDownloadStickers();
  const toast = useToast();
  const busy = make.isPending || download.isPending;

  const count = preset === 'custom' ? Number(custom) : preset;
  const countError = preset === 'custom' && custom !== '' && (!Number.isInteger(count) || count < 1 || count > STICKER_BATCH_MAX) ? `A whole number from 1 to ${STICKER_BATCH_MAX}` : '';
  const valid = Number.isInteger(count) && count >= 1 && count <= STICKER_BATCH_MAX;

  const go = async () => {
    let made: { batch: number; ids: string[] };
    try {
      made = await make.mutateAsync(count);
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Something went wrong.', 'error');
      return;
    }
    try {
      const name = await download.mutateAsync({ ids: made.ids, theme });
      toast(`Made batch ${made.batch} (${plural(made.ids.length)}) and saved ${name}.`);
    } catch (e) {
      toast(`Made batch ${made.batch}, but the download didn't work: ${e instanceof ApiError ? e.message : 'something went wrong'}. Download it from the list.`, 'error');
    }
    close();
  };

  return (
    <Dialog open onClose={() => !busy && close()} fullWidth maxWidth="sm">
      <DialogTitle>Make new QR stickers</DialogTitle>
      <DialogContent>
        <Typography color="textSecondary" sx={{ mb: 2.5 }}>
          Each sticker gets its own code, for any charger. When you turn on public charging on a charger, you choose which sticker is on each socket.
        </Typography>
        <Typography sx={{ fontWeight: 500, mb: 1 }}>How many?</Typography>
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          {STICKER_BATCH_PRESETS.map((n) => (
            <ChoiceChip key={n} label={String(n)} selected={preset === n} onClick={() => setPreset(n)} />
          ))}
          <ChoiceChip label="Custom" selected={preset === 'custom'} onClick={() => setPreset('custom')} />
          {preset === 'custom' && (
            <TextField
              size="small"
              autoFocus
              value={custom}
              onChange={(e) => setCustom(e.target.value.replace(/\D/g, ''))}
              error={!!countError}
              helperText={countError || ' '}
              placeholder="e.g. 500"
              slotProps={{ htmlInput: { inputMode: 'numeric', 'aria-label': 'How many stickers' } }}
              sx={{ width: 140, mb: -2.5 }}
            />
          )}
        </Stack>
        <Typography sx={{ fontWeight: 500, mt: 3, mb: 1 }}>Look</Typography>
        <ThemePicker value={theme} onChange={setTheme} />
        <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
          You can download them again later, in either look.{count > 100 ? ' Making this many PDFs takes a little while (about 5 seconds per 100).' : ''}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => close()} disabled={busy}>Cancel</Button>
        <Button variant="contained" startIcon={<Download size={20} strokeWidth={1.75} />} onClick={() => void go()} disabled={busy || !valid}>
          {make.isPending ? 'Making codes…' : download.isPending ? 'Making PDFs…' : valid ? `Make ${plural(count)} and download` : 'Make and download'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** One sticker as it will print, light or dark, with where it is and a Download button. */
export function StickerPreviewDialog({ sticker: s, close }: { sticker: QrSticker } & DialogProps) {
  const [theme, setTheme] = useState<StickerTheme>('light');
  const download = useDownloadStickers();
  const toast = useToast();
  const code = formatChargeCode(s.code);
  const go = async () => {
    try {
      toast(`Saved ${await download.mutateAsync({ ids: [s.id], theme })}.`);
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Something went wrong.', 'error');
    }
  };
  return (
    <Dialog open onClose={() => close()} fullWidth maxWidth="sm" slotProps={{ paper: { sx: { maxWidth: 580 } } }}>
      <DialogTitle>Sticker {code}</DialogTitle>
      <DialogContent>
        <Typography color="textSecondary" sx={{ mb: 2 }}>
          {s.charger ? `On ${stickerPlace(s.charger)} at ${stickerSite(s.charger)}${s.charger.isActive ? '' : ' (charger deactivated)'}.` : 'Not on a charger yet.'}
          {s.batch != null && ` Batch ${s.batch}.`}
        </Typography>
        <Box role="radiogroup" aria-label="Sticker look" sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <ChoiceChip label="Light" selected={theme === 'light'} onClick={() => setTheme('light')} />
          <ChoiceChip label="Dark" selected={theme === 'dark'} onClick={() => setTheme('dark')} />
        </Box>
        {/* The frame is the sticker's shape, centred on a dark mat, so the page sits in the middle. */}
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#2B2B2B', borderRadius: 1, p: 3 }}>
          <PdfPreview url={`/api/qr-stickers/${s.id}/preview?theme=${theme}`} title={`Sticker ${code}, ${theme}`} height="min(60vh, 560px)" />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => close()}>Close</Button>
        <Button variant="contained" startIcon={<Download size={20} strokeWidth={1.75} />} onClick={() => void go()} disabled={download.isPending}>
          {download.isPending ? 'Making the PDF…' : 'Download PDF'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
